import { useComments } from '@/composables/useComments';
import { useNodeManager } from '@/composables/useNodeManager';
import { useSelectionStore } from '@/stores/ui/selection';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer.js';
import { cameraControls, resetCameraToDefault } from '@/threejs/core';
import { changeTransformControlsAll, changeTransformControlsWorld } from '@/threejs/handlers/transform';
import { getObjectByCode, getObjectWidth } from '@/threejs/objectManager';
import { useConfirm } from '@disguise/cloud-ui';
import { MathUtils } from 'three';
import { onBeforeUnmount, onMounted } from 'vue';

export const useKeyboardShortcuts = () => {
  const selectionStore = useSelectionStore();
  const { removeNode, copyAndPasteNode } = useNodeManager();
  const { findNodeById } = useNodeManager();
  const { openNewCommentDialog } = useComments();
  const { confirmDialog } = useConfirm();

  const timelinePlayerStore = useTimelinePlayerStore();

  let copiedNodeId = null;
  let pasteCount = 1;
  let copiedObjectWidth = 1;
  const baseOffset = { x: 1, y: 0, z: 0 };
  const getSelectedNode = () => {
    if (!selectionStore.selectedObjectId) return null;
    return findNodeById(selectionStore.selectedObjectId);
  };

  const isScaleDisabled = () => {
    const node = getSelectedNode();
    return node?.type === 'projector';
  };

  const setTransformMode = (mode) => {
    selectionStore.selectedObjectTransformType = mode;
    changeTransformControlsAll(mode, selectionStore.selectedObjectTransformTarget);
  };

  const toggleWorldLocal = () => {
    selectionStore.selectedObjectTransformWorld =
      selectionStore.selectedObjectTransformWorld === 'world' ? 'local' : 'world';
    changeTransformControlsWorld(selectionStore.selectedObjectTransformWorld);
  };

  const toggleTargetMode = () => {
    selectionStore.selectedObjectTransformTarget = !selectionStore.selectedObjectTransformTarget;
    changeTransformControlsAll(
      selectionStore.selectedObjectTransformType || 'translate',
      selectionStore.selectedObjectTransformTarget
    );
  };

  const handleKeyDown = (event) => {
    // Only handle keyboard shortcuts if weevent.key === 'c')event.key === 'c')'re not in an input field
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return;
    }

    //move: E key
    if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
      if (selectionStore.selectedObjectId) setTransformMode('translate');
    }

    if (event.key === 'r' || event.key === 'R') {
      event.preventDefault();
      if (selectionStore.selectedObjectId) setTransformMode('rotate');
    }

    if (event.key === 't' || event.key === 'T') {
      // console.log('T key pressed')
      event.preventDefault();
      if (selectionStore.selectedObjectId) setTransformMode('scale');
    }

    if (event.key === 'q' || event.key === 'Q') {
      event.preventDefault();
      if (selectionStore.selectedObjectId) {
        selectionStore.selectedObjectTransformType = null;
      }
    }

    if (event.key === 'x' || event.key === 'X') {
      event.preventDefault();
      if (selectionStore.selectedObjectId) toggleWorldLocal();
    }

    if (event.key === 'y' || event.key === 'Y') {
      event.preventDefault();
      if (selectionStore.selectedObjectId) toggleTargetMode();
    }

    // Delete: Delete or Backspace
    if ((event.key === 'Delete' || event.key === 'Backspace') && selectionStore.selectedObjectId) {
      event.preventDefault();

      confirmDialog('Confirmation', 'Are you sure you want to delete this object?', 'Delete', 'Cancel')
        .then((response) => {
          if (response) {
            removeNode(selectionStore.selectedObjectId);
          }
        })
        .catch((err) => {});
    }

    if (event.code === 'Space') {
      event.preventDefault();
      timelinePlayerStore.togglePlay();
    }

    // Rotate: right arrow key
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (cameraControls) {
        cameraControls.rotate(-45 * MathUtils.DEG2RAD, 0, true);
      }
    }

    // Rotate: left arrow key
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (cameraControls) {
        cameraControls.rotate(45 * MathUtils.DEG2RAD, 0, true);
      }
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (cameraControls) {
        cameraControls.rotate(0, 45 * MathUtils.DEG2RAD, true);
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (cameraControls) {
        cameraControls.rotate(0, -45 * MathUtils.DEG2RAD, true);
      }
    }

    // WASD camera movement
    if (event.key === 'w' || event.key === 'W') {
      event.preventDefault();
      if (cameraControls) {
        cameraControls.forward(1, true);
      }
    }

    if (event.key === 's' || event.key === 'S') {
      event.preventDefault();
      if (cameraControls) {
        cameraControls.forward(-1, true);
      }
    }

    if (event.key === 'a' || event.key === 'A') {
      event.preventDefault();
      if (cameraControls) {
        cameraControls.truck(-1, 0, true);
      }
    }

    if (event.key === 'd' || event.key === 'D') {
      event.preventDefault();
      if (cameraControls) {
        cameraControls.truck(1, 0, true);
      }
    }

    // if (event.key === 'c' && (event.metaKey || event.ctrlKey)) {
    //   // event.preventDefault()
    //   //openNewCommentDialog()
    // }

    // Create Component: Shift + Cmd/Ctrl + C
    if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
      const selectedId = selectionStore.selectedObjectId;
      if (selectedId) {
        copiedNodeId = selectedId;
        pasteCount = 1;
        const selectedObject = getObjectByCode(selectedId);
        // console.log('Selected object for copying:', selectedObject)
        if (selectedObject && selectedObject.userData.type !== 'projector') {
          copiedObjectWidth = getObjectWidth(selectedObject);
          // console.log(`Calculated width of copied object: ${copiedObjectWidth}`)
        } else {
          copiedObjectWidth = 1;
        }
      }
      // console.log(`Copied node ${copiedNodeId} with width ${copiedObjectWidth}`)
    }

    // Paste: Ctrl/Cmd+V
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      if (copiedNodeId) {
        const selectedObject = getObjectByCode(copiedNodeId);
        let offset;
        if (selectedObject && selectedObject.userData.type !== 'projector') {
          offset = {
            x: (copiedObjectWidth + baseOffset.x) * pasteCount,
            y: baseOffset.y * pasteCount,
            z: baseOffset.z * pasteCount,
          };
        } else {
          offset = {
            x: baseOffset.x * pasteCount,
            y: baseOffset.y * pasteCount,
            z: baseOffset.z * pasteCount,
          };
        }
        // console.log('Calculated offset for pasted node:', offset)
        copyAndPasteNode(copiedNodeId, offset);
        pasteCount += 1;
        // console.log(`Pasted node ${copiedNodeId} with offset`, offset)
      }
    }

    if (event.key === 'd' && (event.metaKey || event.ctrlKey) && selectionStore.selectedObjectId) {
      event.preventDefault();
      openCreateComponentDialog(selectionStore.selectedObjectId);
    }

    if (event.key === 'F12' || event.key === 'f12' || event.key === 'Escape' || event.key === 'escape') {
      event.preventDefault();
      resetCameraToDefault();
    }
  };

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyDown);
  });
};
